import React from 'react';
import { Link } from 'react-router-dom';
import { Play } from 'lucide-react';
import { useI18n } from '@/lib/i18n';
import { formatTime, formatDays } from '@/lib/schedule';
import { getSlideImages, STATION_HERO_IMAGE } from '@/lib/heroSlides';
import OnAirPill from '@/components/yakaleo/OnAirPill';
import NowPlayingCard from '@/components/yakaleo/NowPlayingCard';
import LiveProgress from '@/components/yakaleo/LiveProgress';

/**
 * On Air Now card for the Schedule page — three states:
 *  1. A show is live → full-bleed banner artwork + ON AIR badge + track info.
 *  2. A show starts within 2 hours → same artwork + amber countdown.
 *  3. Nothing live → "Normal Programming" over the station hero image.
 *
 * `upcoming` is { show, minutes, host } from findUpcomingShow, or null.
 */
export default function OnAirCard({ onAir, host, liveData, onListen, upcoming }) {
  const { lang } = useI18n();
  const es = lang === 'es';
  const np = liveData?.now_playing;

  const gradient =
    'bg-[linear-gradient(180deg,rgba(0,0,0,0.15)_0%,rgba(0,0,0,0.5)_50%,rgba(0,0,0,0.92)_100%)] md:bg-[linear-gradient(90deg,rgba(0,0,0,0.92)_0%,rgba(0,0,0,0.7)_35%,rgba(0,0,0,0)_70%)]';

  function getBg(show) {
    const slideImages = getSlideImages(show.slug);
    return slideImages
      ? slideImages
      : show.artwork_url
        ? { desktop: show.artwork_url, tablet: show.artwork_url, mobile: show.artwork_url }
        : { desktop: STATION_HERO_IMAGE, tablet: STATION_HERO_IMAGE, mobile: STATION_HERO_IMAGE };
  }

  // State 1: Show is live
  if (onAir) {
    const bg = getBg(onAir);
    const showUrl = onAir.slug
      ? (es ? `/programa/${onAir.slug}` : `/en/show/${onAir.slug}`)
      : '/schedule';

    return (
      <div className="relative rounded-2xl overflow-hidden mb-8 min-h-[340px] md:min-h-[380px] flex items-end md:items-center">
        <picture className="absolute inset-0 w-full h-full">
          <source media="(max-width: 767px)" srcSet={bg.mobile} />
          <source media="(max-width: 1279px)" srcSet={bg.tablet} />
          <img src={bg.desktop} alt={onAir.name} fetchPriority="high" decoding="async" className="w-full h-full object-cover object-center" />
        </picture>
        <div className={`absolute inset-0 ${gradient}`} />
        <div className="relative z-10 p-6 md:p-10 w-full md:max-w-2xl">
          <OnAirPill es={es} className="mb-3" />
          <h2 className="text-white text-2xl md:text-4xl font-black mb-1">
            <Link to={showUrl} className="hover:text-purple-300 transition-colors">{onAir.name}</Link>
          </h2>
          {host && (
            host?.id ? (
              <Link
                to={`/hosts/${host.id}`}
                className="inline-block mb-3 text-[clamp(1rem,2.5vw,2.25rem)] font-bold leading-none transition-opacity hover:opacity-80"
              >
                <span className="text-purple-400">{es ? 'con' : 'with'}</span>
                <span className="text-white"> {host.name}</span>
              </Link>
            ) : (
              <p className="mb-3 text-[clamp(1rem,2.5vw,2.25rem)] font-bold leading-none">
                <span className="text-purple-400">{es ? 'con' : 'with'}</span>
                <span className="text-white"> {host.name}</span>
              </p>
            )
          )}
          <LiveProgress show={onAir} es={es} className="mb-4" />
          <NowPlayingCard nowPlaying={np} show={onAir} es={es} className="mb-5" />
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={onListen}
              className="inline-flex items-center gap-2 px-7 py-3 bg-white text-purple-700 font-bold tracking-[0.2em] text-xs md:text-sm rounded-full hover:bg-purple-50 transition-all shadow-lg"
            >
              <Play size={16} fill="currentColor" />
              {es ? 'ESCUCHAR EN VIVO' : 'LISTEN LIVE'}
            </button>
            <Link
              to={showUrl}
              className="inline-flex items-center gap-2 px-7 py-3 border border-white/70 text-white font-bold tracking-[0.2em] text-xs md:text-sm rounded-full hover:bg-white/10 transition-all"
            >
              {es ? 'VER EL PROGRAMA' : 'VIEW SHOW'} →
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // State 2: Show starts within 2 hours (countdown)
  if (upcoming) {
    const show = upcoming.show;
    const minutes = upcoming.minutes;
    const bg = getBg(show);
    const showUrl = show.slug
      ? (es ? `/programa/${show.slug}` : `/en/show/${show.slug}`)
      : '/schedule';
    const countdownText = minutes <= 1
      ? (es ? 'Empieza ya' : 'Starting now')
      : (es ? `Empieza en ${minutes} min` : `Starts in ${minutes} min`);

    return (
      <div className="relative rounded-2xl overflow-hidden mb-8 min-h-[340px] md:min-h-[380px] flex items-end md:items-center">
        <picture className="absolute inset-0 w-full h-full">
          <source media="(max-width: 767px)" srcSet={bg.mobile} />
          <source media="(max-width: 1279px)" srcSet={bg.tablet} />
          <img src={bg.desktop} alt={show.name} fetchPriority="high" decoding="async" className="w-full h-full object-cover object-center" />
        </picture>
        <div className={`absolute inset-0 ${gradient}`} />
        <div className="relative z-10 p-6 md:p-10 w-full md:max-w-2xl">
          <div className="flex items-center gap-2 mb-3">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500"></span>
            </span>
            <span className="text-amber-400 text-xs font-bold tracking-widest">{countdownText}</span>
          </div>
          <h2 className="text-white text-2xl md:text-4xl font-black mb-2">
            <Link to={showUrl} className="hover:text-purple-300 transition-colors">{show.name}</Link>
          </h2>
          {upcoming.host && (
            <p className="text-purple-300 text-sm font-semibold mb-1">
              {es ? `Con ${upcoming.host.name}` : `With ${upcoming.host.name}`}
            </p>
          )}
          <p className="text-white/60 text-sm mb-5">
            {formatDays(show.days, lang)} · {formatTime(show.start_time)} – {formatTime(show.end_time)} (ET)
          </p>
          <button
            onClick={onListen}
            className="inline-flex items-center gap-2 px-7 py-3 bg-white text-purple-700 font-bold tracking-[0.2em] text-xs md:text-sm rounded-full hover:bg-purple-50 transition-all shadow-lg"
          >
            <Play size={16} fill="currentColor" />
            {es ? 'ESCUCHAR EN VIVO' : 'LISTEN LIVE'}
          </button>
        </div>
      </div>
    );
  }

  // State 3: Nothing live, nothing upcoming
  return (
    <div className="relative rounded-2xl overflow-hidden mb-8 min-h-[340px] md:min-h-[380px] flex items-end md:items-center">
      <img
        src={STATION_HERO_IMAGE}
        alt="Yakaleo"
        fetchPriority="high"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover object-center"
      />
      <div className={`absolute inset-0 ${gradient}`} />
      <div className="relative z-10 p-6 md:p-10 w-full md:max-w-xl">
        <h2 className="text-white text-2xl md:text-4xl font-black mb-2">
          {es ? 'Programación Normal' : 'Normal Programming'}
        </h2>
        <p className="text-white/60 text-sm mb-5">Reggaetón 24/7</p>
        <button
          onClick={onListen}
          className="inline-flex items-center gap-2 px-7 py-3 bg-white text-purple-700 font-bold tracking-[0.2em] text-xs md:text-sm rounded-full hover:bg-purple-50 transition-all shadow-lg"
        >
          <Play size={16} fill="currentColor" />
          {es ? 'ESCUCHAR EN VIVO' : 'LISTEN LIVE'}
        </button>
      </div>
    </div>
  );
}